"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

export default function CustomerReviews() {
  const heroReveal = useRevealOnScroll<HTMLDivElement>();
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  const carouselReveal = useRevealOnScroll<HTMLDivElement>();

  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  const secondaryTestimonials = [
    {
      quote:
        "Drip District has completely redefined my streetwear wardrobe. The quality of the fabrics is simply exceptional.",
      author: "SARAH M.",
      meta: "STYLE ENTHUSIAST / LONDON",
    },
    {
      quote:
        "I love how easy it is to find the perfect outfit for any occasion. Their collections are always on trend.",
      author: "JESSICA T.",
      meta: "FASHION BLOGGER / NEW YORK",
    },
    {
      quote:
        "The shopping experience is seamless and the delivery is incredibly fast. Highly recommended!",
      author: "EMILY R.",
      meta: "LOYAL CUSTOMER / SYDNEY",
    },
    {
      quote:
        "Every piece I've purchased feels like it was made just for me. Truly premium quality.",
      author: "CHLOE D.",
      meta: "DESIGNER / PARIS",
    },
    {
      quote:
        "Drip District is my go-to for seasonal drops. Their attention to detail is unmatched.",
      author: "BELLA K.",
      meta: "TRENDSETTER / MILAN",
    },
    {
      quote:
        "From the website design to the packaging, everything feels luxurious and thoughtful.",
      author: "SOPHIA L.",
      meta: "CURATOR / TOKYO",
    },
  ];

  React.useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <section data-section-id="4681"
      id="customerreviews"
      className="bg-background text-foreground py-24 md:py-36 lg:py-44 px-6 md:px-10 overflow-hidden relative"
    >
      {}
      <div
        ref={headerReveal.ref}
        className={`max-w-[1280px] mx-auto transition-all duration-700 ease-out ${
          headerReveal.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-6"
        }`}
      >
        <div className="flex justify-center">
          <span
            className="font-default text-xs tracking-[0.3em] uppercase text-primary"
          >
            — 05 / CUSTOMER REVIEWS
          </span>
        </div>

        {}
        <h2 className="font-default font-bold mt-10 md:mt-14 text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-[1.02] tracking-tight text-foreground">
          What our community{" "}
          <span className="font-default italic font-normal">says.</span>
        </h2>
      </div>

      {}
      <div
        ref={heroReveal.ref}
        className={`max-w-[1200px] mx-auto mt-14 md:mt-20 transition-all duration-1000 ease-out ${
          heroReveal.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div
          className="relative backdrop-blur-sm border border-border p-8 md:p-12 lg:p-16 min-h-[240px] md:min-h-[280px] shadow-lg rounded-lg"
          style={{
            backgroundColor: "var(--background)",
          }}
        >
          {}
          <div
            aria-hidden="true"
            className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary"
          />

          {}
          <Quote
            aria-hidden="true"
            className="absolute top-6 left-8 md:top-8 md:left-12 w-16 h-16 md:w-24 md:h-24 lg:w-[120px] lg:h-[120px] opacity-20 text-primary"
            strokeWidth={1}
          />

          <div className="relative pt-20 md:pt-24 lg:pt-12 lg:pl-32">
            <p className="font-default italic text-2xl sm:text-3xl md:text-4xl lg:text-[32px] leading-[1.4] max-w-[800px] text-foreground">
              Drip District has completely transformed my street style. Every piece is a testament to cutting-edge design and street-ready prestige.
            </p>

            <div
              className="mt-8 md:mt-10 font-default text-[11px] md:text-xs tracking-[0.25em] text-muted-foreground"
            >
              ALEXA V. / PREMIUM MEMBER / LONDON
            </div>
          </div>
        </div>
      </div>

      {}
      <div
        ref={carouselReveal.ref}
        className={`max-w-[1200px] mx-auto mt-16 md:mt-20 transition-all duration-1000 ease-out ${
          carouselReveal.isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <Carousel
          opts={{ align: "start", loop: true }}
          setApi={setApi}
          className="w-full"
        >
          <CarouselContent className="-ml-6">
            {secondaryTestimonials.map((item, i) => (
              <CarouselItem
                key={i}
                data-index={i}
                className="pl-6 basis-full sm:basis-1/2 lg:basis-[380px]"
              >
                <div
                  className="relative backdrop-blur-sm border border-border h-[280px] p-7 md:p-8 flex flex-col justify-between shadow-md rounded-lg"
                  style={{
                    backgroundColor: "var(--background)",
                  }}
                >
                  <div
                    aria-hidden="true"
                    className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary"
                  />
                  <Quote
                    aria-hidden="true"
                    className="w-8 h-8 text-primary opacity-40"
                    strokeWidth={1.5}
                  />
                  <p className="font-default italic text-lg md:text-xl leading-[1.45] text-foreground flex-1 mt-4">
                    {item.quote}
                  </p>
                  <div
                    className="mt-6 font-default text-[10px] tracking-[0.25em] text-muted-foreground"
                  >
                    {item.author} / {item.meta}
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {}
          <div className="flex items-center justify-between mt-10 md:mt-12">
            <div
              className="font-default text-xs tracking-[0.25em] text-muted-foreground"
            >
              {String(current + 1).padStart(2, "0")} /{" "}
              {String(count || secondaryTestimonials.length).padStart(2, "0")}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous testimonial"
                onClick={() => api?.scrollPrev()}
                className="h-12 w-12 border border-border hover:bg-secondary transition-colors text-primary"
              >
                <ChevronLeft className="!w-8 !h-8" strokeWidth={1.5} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next testimonial"
                onClick={() => api?.scrollNext()}
                className="h-12 w-12 border border-border hover:bg-secondary transition-colors text-primary"
              >
                <ChevronRight className="!w-8 !h-8" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
}